import get from 'get-value'
import { MutableRefObject, useEffect, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import { FeaturesConfigDTO } from '../../../NonReact/Types/Types'
import { isStringNotEmpty } from '../../../NonReact/utils/GlobalFunctions'
import { getFeaturesConfig } from '../API/GenericConfigAPI'
import { ReactState } from '../Contexts'
import { JSONResponse } from '../../../NonReact/common/Types/Types'

/**
 * Feature flag hook to get the feature flag from the server.
 *
 * @param featureFlagKey - The key of the feature flag to get. Feature flags are stored in the file 'features.yaml'.
 *                         The key value can be used to get object sub items by using a '.' to separate the keys.
 * @param defaultValue - The default value to use if the feature flag is not found.
 *
 * @returns The feature flag value.
 */
export const useFeatureFlag = <T extends Definable<boolean>>(
    featureFlagKey: string,
    defaultValue?: T
): T extends boolean ? boolean : Definable<boolean> => {
    const [featureFlag, setFeatureFlag]: ReactState<Definable<boolean>> = useState<Definable<boolean>>(defaultValue)

    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    useEffect((): void => {
        if (isStringNotEmpty(featureFlagKey)) {
            // Get the feature flags from the server.
            getFeaturesConfig()
                .then((response: JSONResponse<FeaturesConfigDTO>): void => {
                    if (isMounted.current && response) {
                        // Check the server response for the feature flag.
                        setFeatureFlag(!!get(response, featureFlagKey) ?? false)
                    }
                })
                .catch((): void => {
                    if (isMounted.current) {
                        // If there is an error getting the feature flags from the server, set the feature flag to the default value.
                        setFeatureFlag(defaultValue ?? false)
                    }
                })
        }
    }, [featureFlagKey])

    return (featureFlag ?? defaultValue) as T extends boolean ? boolean : Definable<boolean>
}
